import { yupResolver } from "@hookform/resolvers/yup";
import React, { useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AiOutlineClose } from "react-icons/ai";
import * as Yup from "yup";
import YupPassword from "yup-password";

import { useUpdatePasswordMutation } from "../../../../app/services/auth-rtk.service";
import Alert, { IAlertType } from "../../../../components/common/Alert";
import BsInputPassword from "../../../../components/common/BsInputPassword";
import { CheckIcon } from "../../../../components/common/icons/check.icon";
import { UnCheckIcon } from "../../../../components/common/icons/uncheck.icon";
import { AlertsType } from "../../../../constants/alerts.constant";

YupPassword(Yup);

const ProfileChangePassword = ({
  showModal,
  setShowModal
}: {
  showModal: boolean;
  setShowModal?: (show: boolean) => void;
}) => {
  const { t } = useTranslation();
  const schema = Yup.object({
    user_current_password: Yup.string().required(t(AlertsType.ISPFKO002)),
    user_new_password: Yup.string()
      .required(t(AlertsType.ISPFKO003))
      .min(8, t(AlertsType.ISATKO004))
      .minLowercase(1, t(AlertsType.ISATKO005))
      .minUppercase(1, t(AlertsType.ISATKO006))
      .minSymbols(1, t(AlertsType.ISATKO007))
      .minNumbers(1, t(AlertsType.ISATKO008)),
    user_repeat_password: Yup.string().oneOf(
      [Yup.ref("user_new_password")],
      t(AlertsType.ISPFKO004)
    )
  });
  const [alert, setAlert] = useState<IAlertType>({
    isShow: false,
    title: "",
    type: "warning",
    message: ""
  });

  const [updateProfilePassword, { isLoading }] = useUpdatePasswordMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      user_current_password: "",
      user_new_password: "",
      user_repeat_password: ""
    },
    resolver: yupResolver(schema),
    criteriaMode: "all",
    mode: "all"
  });

  const onSubmit = (data: any) => {
    closeAlert();
    updateProfilePassword({
      actual_password: data.user_current_password,
      new_password: data.user_new_password
    })
      .unwrap()
      .then(() => {
        setAlert({
          isShow: true,
          message: t(AlertsType.ISPFKO005),
          type: "success",
          title: ""
        });
        reset({
          user_current_password: "",
          user_new_password: "",
          user_repeat_password: ""
        });
      })
      .catch((res) => {
        if (res.status != 500 && res.data) {
          setAlert({
            ...alert,
            isShow: true,
            message: res.data.message
          });
        }
      });
  };

  const closeAlert = () => {
    setAlert({
      isShow: false,
      title: "",
      type: "warning",
      message: ""
    });
  };

  const close = () => {
    reset({
      user_current_password: "",
      user_new_password: "",
      user_repeat_password: ""
    });
    setShowModal?.(false);
  };

  const renderValidationBlock = () => {
    return (
      <Row className="tw-w-full tw-m-0 tw-rounded-xl bg-twentyTwoColor tw-px-5 tw-py-8">
        <Col lg="12">
          <span className="tw-text-sm tw-font-bold font-sofiaSans">
            {t("ProfileChangePassword/t-01")}
          </span>
        </Col>
        <Col lg="12" className="d-flex flex-column gap-3 tw-mt-8">
          {errors?.user_new_password?.types?.min &&
          errors?.user_new_password?.types?.required ? (
            <span className="color-forthColor tw-font-medium tw-text-xs">
              <UnCheckIcon /> {t("ProfileChangePassword/l-04")}
            </span>
          ) : (
            <span className="color-fifteenColor tw-font-medium tw-text-xs">
              <CheckIcon />
              {t("ProfileChangePassword/l-04")}
            </span>
          )}
          {errors?.user_new_password?.types?.minUppercase ? (
            <span className="color-forthColor tw-font-medium tw-text-xs">
              <UnCheckIcon /> {t("ProfileChangePassword/l-05")}
            </span>
          ) : (
            <span className="color-fifteenColor tw-font-medium tw-text-xs">
              <CheckIcon /> {t("ProfileChangePassword/l-05")}
            </span>
          )}
          {errors?.user_new_password?.types?.minLowercase ? (
            <span className="color-forthColor tw-font-medium tw-text-xs">
              <UnCheckIcon /> {t("ProfileChangePassword/l-06")}
            </span>
          ) : (
            <span className="color-fifteenColor tw-font-medium tw-text-xs">
              <CheckIcon /> {t("ProfileChangePassword/l-06")}
            </span>
          )}

          {errors?.user_new_password?.types?.minSymbol ? (
            <span className="color-forthColor tw-font-medium tw-text-xs">
              <UnCheckIcon /> {t("ProfileChangePassword/l-07")}
            </span>
          ) : (
            <span className="color-fifteenColor tw-font-medium tw-text-xs">
              <CheckIcon />
              {t("ProfileChangePassword/l-07")}
            </span>
          )}

          {errors?.user_new_password?.types?.minNumber ? (
            <span className="color-forthColor tw-font-medium tw-text-xs">
              <UnCheckIcon /> {t("ProfileChangePassword/l-08")}
            </span>
          ) : (
            <span className="color-fifteenColor tw-font-medium tw-text-xs">
              <CheckIcon /> {t("ProfileChangePassword/l-08")}
            </span>
          )}
        </Col>
      </Row>
    );
  };

  return (
    <>
      {showModal && (
        <Modal show={showModal} centered onHide={() => close()} size="lg">
          <Modal.Body className="tw-p-5">
            <Row className="m-0">
              <Col className="tw-flex tw-justify-between tw-items-start tw-px-0">
                <div>
                  <h4 className="m-0 mt-2 font-sofiaSans tw-font-bold tw-text-4xl ">
                    {t("ProfileEditPage/l-03")}
                  </h4>
                  <p className="tw-m-0 tw-mt-2 tw-text-lg font-sofiaSans tw-font-bold">
                    {t("ProfileEditPage/l-06")}
                  </p>
                </div>
                <AiOutlineClose
                  size={24}
                  className="tw-cursor-pointer tw-text-neutral-400"
                  onClick={() => close()}
                />
              </Col>
            </Row>

            {alert.isShow && (
              <Row className="tw-mt-3">
                <Col>
                  <Alert
                    type={alert.type ?? "warning"}
                    onClick={() => closeAlert()}
                  >
                    {alert.message}
                  </Alert>
                </Col>
              </Row>
            )}

            <Form onSubmit={handleSubmit(onSubmit)} className="w-100">
              <Row className="tw-mt-5 dashboard-profile-edit-account-content">
                <Col md="6" sm="12">
                  <Row className="">
                    <Col>
                      <Form.Group>
                        <BsInputPassword
                          label={t("ProfileChangePassword/l-01")}
                          isInvalid={!!errors.user_current_password}
                          errorMessage={errors.user_current_password?.message}
                          rhfControls={register("user_current_password")}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("ProfileChangePassword/l-01")}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="tw-mt-8">
                    <Col>
                      <Form.Group>
                        <BsInputPassword
                          label={t("ProfileChangePassword/l-02")}
                          isInvalid={!!errors.user_new_password}
                          errorMessage={errors.user_new_password?.message}
                          rhfControls={register("user_new_password")}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("ProfileChangePassword/l-02")}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="tw-mt-8 md:tw-mt-0 tw-block md:tw-hidden">
                    <Col md="6" sm="12">
                      {renderValidationBlock()}
                    </Col>
                  </Row>
                  <Row className="tw-mt-8">
                    <Col>
                      <Form.Group>
                        <BsInputPassword
                          label={t("ProfileChangePassword/l-03")}
                          isInvalid={!!errors.user_repeat_password}
                          errorMessage={errors.user_repeat_password?.message}
                          rhfControls={register("user_repeat_password")}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("ProfileChangePassword/l-03")}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
                <Col
                  md="6"
                  sm="12"
                  className="tw-mt-8 md:tw-mt-0 tw-hidden md:tw-block"
                >
                  {renderValidationBlock()}
                </Col>
              </Row>
              <Row className="dashboard-profile-edit-account-footer">
                <Col className="pt-3 d-flex justify-content-start">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="justify-content-center px-4"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Spinner animation="grow" />
                    ) : (
                      t("ProfileChangePassword/btn-01")
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default ProfileChangePassword;
